<template>
  <div v-if="!patientInfo">
    <div class="tw-text-center tw-text-gray-500 tw-py-6">Loading Patient's Information...</div>
  </div>
  <div v-else>
    <h2
      class="tw-text-xl sm:tw-text-xl tw-text-primary-500 tw-bg-transparent  tw-shadow-none tw-font-sans tw-inline-flex tw-items-center border-0 focus:tw-outline-none focus:tw-text-primary-700"
    >
      Patient's Information
    </h2>

    <div v-if="!isLoading">
      <dl class="tw-divide-y tw-divide-gray-200 tw-mb-0">
        <div class="tw-py-2 sm:tw-py-4 sm:tw-grid sm:tw-grid-cols-4 sm:tw-gap-4">
          <dt class="tw-text-xs sm:tw-text-sm tw-text-gray-500 tw-font-light tw-flex tw-items-center">Legal First and Last Name</dt>
          <dd class="tw-mt-1 tw-flex tw-items-end sm:tw-items-center tw-text-sm tw-text-gray-900 sm:tw-mt-0 sm:tw-col-span-3 tw-mb-0">
            <span class="tw-flex-grow">{{ patientInfo.name.fullName || "-" }}</span>
          </dd>
        </div>

        <div class="tw-py-2 sm:tw-py-4 sm:tw-grid sm:tw-grid-cols-4 sm:tw-gap-4">
          <dt class="tw-text-xs sm:tw-text-sm tw-text-gray-500 tw-font-light tw-flex tw-items-center">Email</dt>
          <dd class="tw-mt-1 tw-flex tw-items-end sm:tw-items-center tw-text-sm tw-text-gray-900 sm:tw-mt-0 sm:tw-col-span-3 tw-mb-0">
            <span class="tw-flex-grow">{{ patientInfo.emailAddress ? patientInfo.emailAddress.address : "-" }}</span>
          </dd>
        </div>

        <div class="tw-py-2 sm:tw-py-4 sm:tw-grid sm:tw-grid-cols-4 sm:tw-gap-4">
          <dt class="tw-text-xs sm:tw-text-sm tw-text-gray-500 tw-font-light tw-flex tw-items-center">Phone</dt>
          <dd class="tw-mt-1 tw-flex tw-items-end sm:tw-items-center tw-text-sm tw-text-gray-900 sm:tw-mt-0 sm:tw-col-span-3 tw-mb-0">
            <span class="tw-flex-grow">{{ formatPhoneNumber(patientInfo.phoneNumber) || "-" }}</span>
          </dd>
        </div>

        <div class="tw-py-2 sm:tw-py-4 sm:tw-grid sm:tw-grid-cols-4 sm:tw-gap-4">
          <dt class="tw-text-xs sm:tw-text-sm tw-text-gray-500 tw-font-light tw-flex tw-items-center">Mailing Address</dt>
          <dd
            class="tw-mt-1 tw-flex tw-items-end sm:tw-items-center tw-text-sm tw-text-gray-900 sm:tw-mt-0 sm:tw-col-span-3 tw-mb-0 tw-whitespace-pre-line"
            :class="{ 'tw-text-red-700': hasAddressMismatch }"
          >
            <span class="tw-flex-grow text-red-400">{{ formatAddress(patientInfo.mailingAddress) || "-" }}</span>
          </dd>
        </div>

        <div class="tw-py-2 sm:tw-py-4 sm:tw-grid sm:tw-grid-cols-4 sm:tw-gap-4">
          <dt class="tw-text-xs sm:tw-text-sm tw-text-gray-500 tw-font-light tw-flex tw-items-center">Gender</dt>
          <dd class="tw-mt-1 tw-flex tw-items-end sm:tw-items-center tw-text-sm tw-text-gray-900 sm:tw-mt-0 sm:tw-col-span-3 tw-mb-0">
            <span class="tw-flex-grow">{{ patientInfo.gender || "-" }}</span>
          </dd>
        </div>

        <div class="tw-py-2 sm:tw-py-4 sm:tw-grid sm:tw-grid-cols-4 sm:tw-gap-4">
          <dt class="tw-text-xs sm:tw-text-sm tw-text-gray-500 tw-font-light tw-flex tw-items-center">Date of Birth</dt>
          <dd class="tw-mt-1 tw-flex tw-items-end sm:tw-items-center tw-text-sm tw-text-gray-900 sm:tw-mt-0 sm:tw-col-span-3 tw-mb-0">
            <span class="tw-pr-2">{{ formatDate(patientInfo.dob) || "-" }}</span>
            <span v-if="patientAge(patientInfo.dob)" :class="patientAge(patientInfo.dob) < 19 ? 'tw-text-red-500 tw-text-sm' : 'tw-text-sm'">
              ({{ patientAge(patientInfo.dob) }} years old)
            </span>
          </dd>
        </div>

        <div class="tw-py-2 sm:tw-py-4 sm:tw-grid sm:tw-grid-cols-4 sm:tw-gap-4">
          <dt class="tw-text-xs sm:tw-text-sm tw-text-gray-500 tw-font-light tw-flex tw-items-center">Last Appointment</dt>
          <dd class="tw-mt-1 tw-flex tw-items-end sm:tw-items-center tw-text-sm tw-text-gray-900 sm:tw-mt-0 sm:tw-col-span-3 tw-mb-0">
            <span class="tw-flex-grow">{{ formatDate(patientInfo.lastAppointmentDate) || "-" }}</span>
          </dd>
        </div>

        <div class="tw-py-2 sm:tw-py-4 sm:tw-grid sm:tw-grid-cols-4 sm:tw-gap-4">
          <dt class="tw-text-xs sm:tw-text-sm tw-text-gray-500 tw-font-light tw-flex tw-items-center">Last Appointment Location</dt>
          <dd class="tw-mt-1 tw-flex tw-items-end sm:tw-items-center tw-text-sm tw-text-gray-900 sm:tw-mt-0 sm:tw-col-span-3 tw-mb-0">
            <span class="tw-flex-grow">{{ patientInfo.appointmentLocation || "-" }}</span>
          </dd>
        </div>

        <div class="tw-py-2 sm:tw-py-4 sm:tw-grid sm:tw-grid-cols-4 sm:tw-gap-4">
          <dt class="tw-text-xs sm:tw-text-sm tw-text-red-600 tw-font-light tw-flex tw-items-center">Known Allergies</dt>
          <dd class="tw-mt-1 tw-flex tw-items-end sm:tw-items-center tw-text-sm tw-text-red-600 sm:tw-mt-0 sm:tw-col-span-3 tw-mb-0">
            <span class="tw-flex-grow">{{ patientInfo.knownAllergies || "-" }}</span>
          </dd>
        </div>
      </dl>
    </div>
    <div v-else class="tw-divide-y tw-divide-gray-200">
      <div class="tw-py-2 sm:tw-py-4 sm:tw-grid sm:tw-grid-cols-4 sm:tw-gap-4" v-for="i in 9" :key="`info-loading-${i}`">
        <base-skeleton-loader class="tw-h-2 sm:tw-my-auto sm:tw-h-3 tw-w-12 tw-mt-1" />
        <dd class="tw-mt-1 tw-flex tw-text-sm tw-text-gray-900 sm:tw-mt-0 sm:tw-col-span-3">
          <div class="tw-flex-grow tw-flex tw-items-center tw-my-px">
            <base-skeleton-loader class="tw-h-3 tw-my-1.5 sm:tw-my-1 tw-w-24 tw-leading-6" />
          </div>
          <span class="tw-ml-4 tw-flex-shrink-0">
            <base-skeleton-loader class="tw-h-5 tw-w-12" />
          </span>
        </dd>
      </div>
    </div>
  </div>
</template>

<script>
import moment from "moment";
export default {
  name: "PatientInformation",
  props: {
    isLoading: Boolean,
    patientInfo: {
      type: Object,
      default: () => ({}),
    },
    hasAddressMismatch: Boolean,
  },
  methods: {
    formatPhoneNumber(phoneNumberObject) {
      if (!phoneNumberObject || !phoneNumberObject.number || !phoneNumberObject.number.nationalNumber) {
        return null;
      }
      const num = phoneNumberObject.number.nationalNumber.toString();
      if (num.length === 10) {
        return `(${num.substring(0, 3)}) ${num.substring(3, 6)}-${num.substring(6, 10)}`;
      }
      return num; // Return as is if not a 10-digit number
    },
    formatDate(dateString) {
      if (!dateString || dateString === "0001-01-01T00:00:00") {
        // Check for default C# DateTime value
        return null;
      }
      // Assuming dateString is in ISO 8601 format (e.g., "YYYY-MM-DDTHH:mm:ss")
      return moment(dateString).format("M/D/YYYY");
    },
    patientAge(dob) {
      if (!dob || dob === "0001-01-01T00:00:00") {
        return null;
      }
      let age = moment().diff(moment(dob), "years");
      return age;
    },
    formatAddress(addressObject) {
      if (!addressObject) return null;
      const parts = [];
      if (addressObject.line1) parts.push(addressObject.line1);
      if (addressObject.line2) parts.push(addressObject.line2);
      const cityStateZip = [];
      if (addressObject.city) cityStateZip.push(addressObject.city);
      if (addressObject.state) cityStateZip.push(addressObject.state);
      if (addressObject.zip) cityStateZip.push(addressObject.zip);
      if (cityStateZip.length > 0) parts.push(cityStateZip.join(", "));
      return parts.join("\n");
    },
  },
};
</script>
